import { suuid } from "@zetalabs/core";

type Batch = string[];

type Fn = (...args: any[]) => any;
type AsyncFn = (...args: any[]) => Promise<any>;

const isAsync = (fn: Function) => fn.constructor.name === "AsyncFunction";

enum ThreadStatus {
	Pending = "pending",
	Running = "running",
	Done = "done",
	Error = "error",
	Cancelled = "cancelled",
}

interface Thread<T extends Fn | AsyncFn> {
	id: string;
	status: ThreadStatus;
	result: ReturnType<T> | null;
	error: string | null;
	startedAt: number;
	finishedAt: number | null;
	eager: boolean;
}

interface SchedulerConfiguration {
	concurrency: number;
}

export class Scheduler {
	private _fns = new Map<string, Fn | AsyncFn>();
	private _promises = new Map<
		string,
		{
			resolve: (value: any) => void;
			reject: (reason?: any) => void;
		}
	>();

	private _threads = new Map<string, Thread<Fn | AsyncFn>>();
	private _pending = new Set<string>();
	private _request: ReturnType<typeof setTimeout> | null = null;

	constructor(public readonly config: SchedulerConfiguration) {}

	public spawn = async <T extends Fn | AsyncFn>(
		fn: T,
		eager = false,
	): Promise<T extends AsyncFn ? Awaited<ReturnType<T>> : ReturnType<T>> => {
		return new Promise((resolve, reject) => {
			const id = suuid();

			const thread: Thread<T> = {
				id,
				status: ThreadStatus.Pending,
				result: null,
				error: null,
				startedAt: Date.now(),
				finishedAt: null,
				eager,
			};

			this._fns.set(id, fn);
			this._promises.set(id, { resolve, reject });
			this._threads.set(thread.id, thread);
			this._pending.add(thread.id);

			if (this._request) {
				clearTimeout(this._request);
				this._request = null;
			}

			if (this._pending.size === this.config.concurrency) {
				const nextBatch: Batch = Array.from(this._pending).slice(
					0,
					this.config.concurrency,
				);
				this._pending.clear();

				this.run(nextBatch);
				return;
			}

			this._request = setTimeout(() => {
				this._request = null;

				const nextBatch: Batch = Array.from(this._pending);
				this._pending.clear();

				this.run(nextBatch);
			}, 50);

			// Randomly free up threads
		});
	};

	private run = async (batch: Batch): Promise<void> => {
		const results = await Promise.allSettled(
			batch.map(async (id) => {
				const thread = this._threads.get(id);

				if (!thread) {
					return;
				}

				thread.status = ThreadStatus.Running;

				const fn = this._fns.get(thread.id);
				const promise = this._promises.get(thread.id);

				if (!fn) {
					return;
				}

				if (thread.eager) {
					if (isAsync(fn)) {
						const result = await fn();
						promise?.resolve(result);
					} else {
						const result = fn();
						promise?.resolve(result);
					}

					thread.finishedAt = Date.now();
					thread.status = ThreadStatus.Done;

					this._threads.delete(id);
					this._promises.delete(thread.id);
				} else {
					return fn();
				}
			}),
		);

		results.forEach((result, index) => {
			const id = batch[index];
			const thread = this._threads.get(id);

			if (!thread) {
				return;
			}

			const promise = this._promises.get(thread.id);

			if (result.status === "fulfilled") {
				thread.status = ThreadStatus.Done;
				thread.result = result.value;

				if (promise) {
					promise.resolve(result.value);
				}
			} else {
				thread.status = ThreadStatus.Error;
				thread.error = result.reason;

				if (promise) {
					promise.reject(result.reason);
				}
			}

			this._promises.delete(thread.id);
			this._threads.delete(thread.id);
		});
	};
}

export const Threads = new Scheduler({ concurrency: 20 });
